'use client';

import { c as _c } from "react/compiler-runtime";
import 'keen-slider/keen-slider.min.css';
import { useKeenSlider } from 'keen-slider/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Container } from '@src/components/Container/Container';
import { type RailProps } from '@src/components/Rail/Rail.props';
import { RailCarouselArrows } from '@src/components/Rail/RailCarousel/RailCarouselArrows';
import { useRailArrowPosition } from '@src/components/Rail/RailCarousel/useRailArrowPosition';
import { RailItem } from '@src/components/Rail/RailItem';
import { useBreakpoints } from '@src/utils/breakpoints.util';
import { mouseWheelPlugin } from '@src/utils/slider.utils';
import { cn } from '@src/utils/tailwind';
type RailCarouselProps = Pick<RailProps, 'items' | 'columnsDesktop' | 'columnsMobile' | 'imageStyle' | 'builderBlock' | 'slug'> & {
  isPreviewing?: boolean;
};
export const RailCarousel = props => {
  const $ = _c(44);
  const {
    items,
    columnsDesktop: t0,
    columnsMobile: t1,
    imageStyle,
    isPreviewing,
    builderBlock,
    slug
  } = props;
  const columnsDesktop = t0 === undefined ? 1 : t0;
  const columnsMobile = t1 === undefined ? 1 : t1;
  const [isLoaded, setIsLoaded] = useState(false);
  const breakpoints = useBreakpoints();
  const isMobile = breakpoints.isMobile || breakpoints.isTablet;
  const [hideLeft, setHideLeft] = useState(false);
  const [hideRight, setHideRight] = useState(false);
  const containerRef = useRef(null);
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsLoaded(true);
    };
    t3 = s => {
      setHideLeft(s.track.details.rel === 0);
      setHideRight(s.track.details.maxIdx === s.track.details.rel);
    };
    t4 = {
      perView: "auto",
      origin: "auto",
      spacing: 12
    };
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t2 = $[0];
    t3 = $[1];
    t4 = $[2];
  }
  let t5;
  if ($[3] !== columnsDesktop) {
    t5 = {
      loop: false,
      mode: "free",
      rubberband: false,
      created: t2,
      slideChanged: t3,
      slides: t4,
      breakpoints: {
        "(min-width: 1023px)": {
          slides: {
            perView: columnsDesktop,
            origin: "auto",
            spacing: 32
          }
        }
      }
    };
    $[3] = columnsDesktop;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [mouseWheelPlugin];
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const [sliderRef, instanceRef] = useKeenSlider(t5, t6);
  const arrowPosition = useRailArrowPosition(instanceRef, containerRef, setHideLeft, setHideRight);
  let t7;
  let t8;
  if ($[6] !== instanceRef) {
    t7 = () => {
      const handleResize = () => {
        instanceRef.current?.update();
      };
      window.addEventListener("resize", handleResize);
      handleResize();
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    };
    t8 = [instanceRef];
    $[6] = instanceRef;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== builderBlock || $[10] !== columnsDesktop || $[11] !== columnsMobile || $[12] !== imageStyle || $[13] !== isLoaded || $[14] !== isMobile || $[15] !== isPreviewing || $[16] !== items || $[17] !== slug) {
    t9 = () => items.map((t10, index) => {
      const {
        blocks
      } = t10;
      const effectiveMobileColumns = columnsMobile * 1.25;
      const width = isMobile ? `${100 / effectiveMobileColumns}%` : undefined;
      return <div key={index} className={cn("keen-slider__slide overflow-visible! px-2", {
        "opacity-0": !isLoaded
      })} style={{
        minWidth: width,
        maxWidth: width
      }}><RailItem index={index} blocks={blocks} imageStyle={imageStyle} isPreviewing={isPreviewing} builderBlock={builderBlock} columnsDesktop={columnsDesktop} slug={slug} /></div>;
    });
    $[9] = builderBlock;
    $[10] = columnsDesktop;
    $[11] = columnsMobile;
    $[12] = imageStyle;
    $[13] = isLoaded;
    $[14] = isMobile;
    $[15] = isPreviewing;
    $[16] = items;
    $[17] = slug;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const renderItems = t9;
  let t10;
  if ($[19] !== isMobile) {
    t10 = () => {
      if (!isMobile) {
        return null;
      }
      return <div className={cn("keen-slider__slide", "max-w-1 min-w-1", "sm:max-w-5 sm:min-w-5")} />;
    };
    $[19] = isMobile;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const renderMobileSpacer = t10;
  let t11;
  if ($[21] !== arrowPosition || $[22] !== hideLeft || $[23] !== hideRight || $[24] !== instanceRef || $[25] !== isLoaded) {
    t11 = isLoaded && <RailCarouselArrows arrowPosition={arrowPosition} previousSlide={() => instanceRef.current?.prev()} nextSlide={() => instanceRef.current?.next()} hideLeft={hideLeft} hideRight={hideRight} />;
    $[21] = arrowPosition;
    $[22] = hideLeft;
    $[23] = hideRight;
    $[24] = instanceRef;
    $[25] = isLoaded;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== renderMobileSpacer) {
    t12 = renderMobileSpacer();
    $[27] = renderMobileSpacer;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== renderItems) {
    t13 = renderItems();
    $[29] = renderItems;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== renderMobileSpacer) {
    t14 = renderMobileSpacer();
    $[31] = renderMobileSpacer;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== sliderRef || $[34] !== t12 || $[35] !== t13 || $[36] !== t14) {
    t15 = <div ref={sliderRef} className="keen-slider pt-4 lg:pt-8">{t12}{t13}{t14}</div>;
    $[33] = sliderRef;
    $[34] = t12;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== t11 || $[39] !== t15) {
    t16 = <div className="relative" ref={containerRef}>{t11}{t15}</div>;
    $[38] = t11;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const content = t16;
  let t17;
  if ($[41] !== content || $[42] !== isMobile) {
    t17 = isMobile ? content : <Container>{content}</Container>;
    $[41] = content;
    $[42] = isMobile;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return t17;
};